import { useRunEstimatedAnnualConsumptionAnalysis } from "@/entities/annualConsumption"
import { ProviderCalendarId } from "@/entities/providerCalendar"
import { type OnboardingState, useOnboardingToStep } from "@/features/onboarding/wizard"
import { toAnnualConsumptionWithTemporal } from "@/features/subscription"
import { logger } from "@/lib/logger"
import { OnboardingRouteNames } from "@/routes/onboarding"
import { useRequestQuote } from "../../requestQuote"

type UseCoconRequestQuoteProps = {
    stepName: OnboardingRouteNames
}

export function useCoconRequestQuote(props: UseCoconRequestQuoteProps) {
    const { toStep } = useOnboardingToStep(props.stepName)

    const [handleRequestQuote, requestQuote] = useRequestQuote(props)

    const runEstimatedAnnualConsumptionAnalysis = useRunEstimatedAnnualConsumptionAnalysis()

    const handleCoconRequestQuote = async (onboardingState: OnboardingState) => {
        const newOnboardingState = structuredClone(onboardingState)
        const { formValues } = newOnboardingState

        if (!formValues.electricity.supplyPoint.meterPoint.providerCalendarId) {
            return await toStep(OnboardingRouteNames.EnergyLandscape, newOnboardingState)
        }

        let annualConsumptionByTemporalClass:
            | Array<{
                  temporalClass: string
                  consumption: number
              }>
            | undefined = undefined

        if (newOnboardingState.formValues.electricity.supplyPoint.meterPoint.serviceLevel === 2) {
            newOnboardingState.formValues.electricity.isSeasonalHphcBase =
                newOnboardingState.formValues.electricity.providerCalendarId ===
                ProviderCalendarId.BASE

            newOnboardingState.formValues.electricity.providerCalendarId =
                ProviderCalendarId.HPHC_2_SAISONS

            if (formValues.journeyType === "COMMISSIONING") {
                return await toStep(OnboardingRouteNames.EnergyLandscape, newOnboardingState)
            }

            try {
                const result = await runEstimatedAnnualConsumptionAnalysis.mutateAsync({
                    meterPointId: formValues.electricity.supplyPoint.meterPoint.id,
                    firstName: formValues.givenName,
                    lastName: formValues.familyName,
                })

                if (result.consumption) {
                    annualConsumptionByTemporalClass = result.consumption
                } else if (result.unavailableReason) {
                    logger.error({
                        error: "Annual consumption analysis unavailable for this PRM/PCE.",
                        meta: {
                            feature: "onboarding",
                            source: "runEstimatedAnnualConsumptionAnalysis",
                            unavailableReason: result.unavailableReason,
                        },
                    })

                    newOnboardingState.formValues.enedisAnalysisToken = result.token

                    return await toStep(OnboardingRouteNames.EnergyLandscape, newOnboardingState)
                }
            } catch (error) {
                logger.error({
                    error,
                    meta: {
                        feature: "onboarding",
                        source: "runEstimatedAnnualConsumptionAnalysis",
                    },
                })

                return await toStep(OnboardingRouteNames.EnergyLandscape, newOnboardingState)
            }
        }

        if (formValues.journeyType === "COMMISSIONING") {
            return await toStep(OnboardingRouteNames.EnergyLandscape, newOnboardingState)
        }

        return await handleRequestQuote({
            onboardingState: newOnboardingState,
            providedAnnualConsumption: {
                electricity: annualConsumptionByTemporalClass,
            },
            annualConsumptionFallback: {
                electricity:
                    newOnboardingState.formValues.electricity.invoiceUpload.extractionUuid &&
                    newOnboardingState.formValues.electricity.annualConsumptionReference
                        ? toAnnualConsumptionWithTemporal(
                              newOnboardingState.formValues.electricity.annualConsumptionReference
                          )
                        : undefined,
                gas:
                    newOnboardingState.formValues.gas.invoiceUpload.extractionUuid &&
                    newOnboardingState.formValues.gas.annualConsumptionReference
                        ? {
                              allDay: newOnboardingState.formValues.gas.annualConsumptionReference,
                          }
                        : undefined,
            },
        })
    }

    return [handleCoconRequestQuote, requestQuote] as const
}
